/**
 * React glue for the client store (PXL). `NetProvider` supplies the store +
 * connection; `useGame()` subscribes a component to the store and returns the
 * current reactive state plus the connection for sending intents.
 */

import { createContext, useContext, useSyncExternalStore } from 'react';
import type { ReactNode } from 'react';

import type { GameConnection } from '../net/connection.js';
import type { ClientState, ClientStore } from '../state/store.js';

interface GameCtx {
  store: ClientStore;
  conn: GameConnection;
}

const Ctx = createContext<GameCtx | null>(null);

export function NetProvider({
  store,
  conn,
  children,
}: GameCtx & { children: ReactNode }): JSX.Element {
  return <Ctx.Provider value={{ store, conn }}>{children}</Ctx.Provider>;
}

/** Subscribe to the store and read the current state + connection. */
export function useGame(): { state: ClientState; conn: GameConnection; store: ClientStore } {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useGame() used outside <NetProvider>');
  // Re-render whenever the store version bumps (any inbound message).
  useSyncExternalStore(ctx.store.subscribe, ctx.store.getVersion, ctx.store.getVersion);
  return { state: ctx.store.state, conn: ctx.conn, store: ctx.store };
}
